import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Alert } from 'react-native';
import { XStack, YStack, Card, Button, Switch } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface ModernProjectSettingsProps {
  project: any;
  onSave: (updates: any) => void;
}

const ModernProjectSettings: React.FC<ModernProjectSettingsProps> = ({
  project,
  onSave
}) => {
  const theme = themeSelector();
  
  const [title, setTitle] = useState(project?.title || '');
  const [description, setDescription] = useState(project?.description || '');
  const [isPublic, setIsPublic] = useState(project?.isPublic || false);
  const [allowComments, setAllowComments] = useState(project?.allowComments !== false);
  const [notifications, setNotifications] = useState(project?.notifications !== false);

  const handleSave = () => {
    onSave({
      title: title.trim(),
      description: description.trim(),
      isPublic,
      allowComments,
      notifications
    });
    Alert.alert('Success', 'Project settings updated');
  };

  const handleDelete = () => {
    Alert.alert(
      'Delete Project',
      'Are you sure you want to delete this project? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: () => {
            // Handle project deletion
            console.log('Delete project');
          }
        }
      ]
    );
  };

  return (
    <ScrollView style={{ 
      flex: 1, 
      backgroundColor: theme ? '#f9fafb' : '#111827',
      padding: 20 
    }}>
      <Text style={{
        fontSize: 20,
        fontWeight: '700',
        color: theme ? '#111827' : '#ffffff',
        marginBottom: 16,
      }}>
        Project Settings
      </Text>

      {/* Basic Information */}
      <Card
        padding="$4"
        backgroundColor={theme ? '#ffffff' : '#1f2937'}
        borderRadius="$3"
        marginBottom="$4"
      >
        <Text style={{
          fontSize: 16,
          fontWeight: '600',
          color: theme ? '#111827' : '#ffffff',
          marginBottom: 16,
        }}>
          Basic Information
        </Text>

        <YStack gap="$4">
          <YStack gap="$2">
            <Text style={{
              fontSize: 14,
              fontWeight: '500',
              color: theme ? '#111827' : '#ffffff',
            }}>
              Project Title
            </Text>
            <TextInput
              value={title}
              onChangeText={setTitle}
              placeholder="Enter project title"
              placeholderTextColor={theme ? '#6b7280' : '#9ca3af'}
              style={{
                fontSize: 16,
                color: theme ? '#111827' : '#ffffff',
                backgroundColor: theme ? '#f9fafb' : '#374151',
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
                borderRadius: 8,
                padding: 12,
              }}
            />
          </YStack>

          <YStack gap="$2">
            <Text style={{
              fontSize: 14,
              fontWeight: '500',
              color: theme ? '#111827' : '#ffffff',
            }}>
              Description
            </Text>
            <TextInput
              value={description}
              onChangeText={setDescription}
              placeholder="Enter project description"
              placeholderTextColor={theme ? '#6b7280' : '#9ca3af'}
              multiline
              numberOfLines={4}
              style={{
                fontSize: 16,
                color: theme ? '#111827' : '#ffffff',
                backgroundColor: theme ? '#f9fafb' : '#374151',
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
                borderRadius: 8,
                padding: 12,
                minHeight: 100,
                textAlignVertical: 'top',
              }}
            />
          </YStack>
        </YStack>
      </Card>

      {/* Privacy & Permissions */}
      <Card
        padding="$4"
        backgroundColor={theme ? '#ffffff' : '#1f2937'}
        borderRadius="$3"
        marginBottom="$4"
      >
        <Text style={{
          fontSize: 16,
          fontWeight: '600',
          color: theme ? '#111827' : '#ffffff',
          marginBottom: 16,
        }}>
          Privacy & Permissions
        </Text>

        <YStack gap="$4">
          <XStack justifyContent="space-between" alignItems="center">
            <YStack flex={1}>
              <Text style={{
                fontSize: 14,
                fontWeight: '500',
                color: theme ? '#111827' : '#ffffff',
              }}>
                Public Project
              </Text>
              <Text style={{
                fontSize: 12,
                color: theme ? '#6b7280' : '#9ca3af',
              }}>
                Allow anyone to view this project
              </Text>
            </YStack>
            <Switch
              checked={isPublic}
              onCheckedChange={setIsPublic}
              size="$2"
            />
          </XStack>

          <XStack justifyContent="space-between" alignItems="center">
            <YStack flex={1}>
              <Text style={{
                fontSize: 14,
                fontWeight: '500',
                color: theme ? '#111827' : '#ffffff',
              }}>
                Allow Comments
              </Text>
              <Text style={{
                fontSize: 12,
                color: theme ? '#6b7280' : '#9ca3af',
              }}>
                Team members can comment on tasks
              </Text>
            </YStack>
            <Switch
              checked={allowComments}
              onCheckedChange={setAllowComments}
              size="$2"
            />
          </XStack>

          <XStack justifyContent="space-between" alignItems="center">
            <YStack flex={1}>
              <Text style={{
                fontSize: 14,
                fontWeight: '500',
                color: theme ? '#111827' : '#ffffff',
              }}>
                Notifications
              </Text>
              <Text style={{
                fontSize: 12,
                color: theme ? '#6b7280' : '#9ca3af',
              }}>
                Receive notifications for project updates
              </Text>
            </YStack>
            <Switch
              checked={notifications}
              onCheckedChange={setNotifications}
              size="$2"
            />
          </XStack>
        </YStack>
      </Card>

      {/* Actions */}
      <Card
        padding="$4"
        backgroundColor={theme ? '#ffffff' : '#1f2937'}
        borderRadius="$3"
        marginBottom="$4"
      >
        <Text style={{
          fontSize: 16,
          fontWeight: '600',
          color: theme ? '#111827' : '#ffffff',
          marginBottom: 16,
        }}>
          Actions
        </Text>

        <YStack gap="$3">
          <Button
            onPress={handleSave}
            backgroundColor="#3b82f6"
            color="#ffffff"
          >
            Save Changes
          </Button>

          <Button
            variant="outlined"
            borderColor={theme ? '#e5e7eb' : '#4b5563'}
            backgroundColor="transparent"
          >
            Export Project
          </Button>

          <Button
            variant="outlined"
            borderColor={theme ? '#e5e7eb' : '#4b5563'}
            backgroundColor="transparent"
          >
            Archive Project
          </Button>

          <Button
            onPress={handleDelete}
            backgroundColor="#ef4444"
            color="#ffffff"
          >
            Delete Project
          </Button>
        </YStack>
      </Card>
    </ScrollView>
  );
};

export default ModernProjectSettings;
